import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Alert, List, ListItem, ListItemIcon, ListItemText, CircularProgress, Paper } from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import api from '../services/api';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/slices/authSlice';
import { StorageManager } from '../utils/storageManager';
import { getAgencyPublicData } from '../services/agencyPublicService';
import type { AgencyPublicData } from '../services/agencyPublicService';

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [agencyData, setAgencyData] = useState<AgencyPublicData | null>(null);
  const [fetchingAgency, setFetchingAgency] = useState(false);

  const token = searchParams.get('token');
  const agencySlug = searchParams.get('agency');
  const isIndividual = searchParams.get('individual') === '1';

  // Fetch agency data if slug is provided
  useEffect(() => {
    const fetchAgencyData = async () => {
      if (!agencySlug) return;
      
      setFetchingAgency(true);
      try {
        const data = await getAgencyPublicData(agencySlug);
        setAgencyData(data);
      } catch (err) {
        console.error('Failed to fetch agency data:', err);
        // Don't show error, just continue without branding
      } finally {
        setFetchingAgency(false);
      }
    };

    fetchAgencyData();
  }, [agencySlug]);

  const validatePassword = (password: string) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return requirements;
  };

  const requirements = validatePassword(form.password);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!token) {
      setError('Invalid or missing token.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!Object.values(requirements).every(Boolean)) {
      setError('Please meet all password requirements.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/auth/set-password', { token, password: form.password });
      const { token: authToken, user, agency } = response.data;
      
      // Store the token
      StorageManager.setToken(authToken);
      
      // Dispatch login success to Redux
      dispatch(loginSuccess({ user, token: authToken, agency }));
      
      // Show success message with agency name
      const agencyName = agency?.name || 'your account';
      setSuccess(`Password set successfully! Redirecting you to ${agencyName}...`);
      
      // Redirect to agency-branded dashboard
      setTimeout(() => {
        if (agency?.slug) {
          navigate(`/agencies/${agency.slug}/dashboard`);
        } else {
          // Fallback to agency login page or regular login
          navigate('/login');
        }
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set password.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingAgency) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ mt: 8, p: 4, textAlign: 'center' }}>
        {/* Agency Banner */}
        {agencyData?.bannerImageData && agencyData?.bannerImageType && (
          <Box
            sx={{
              width: '100%',
              mb: 3,
              borderRadius: 1,
              overflow: 'hidden',
              maxHeight: '200px',
            }}
          >
            <img
              src={`data:${agencyData.bannerImageType};base64,${agencyData.bannerImageData}`}
              alt={`${agencyData.name} banner`}
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        <Typography variant="h4" gutterBottom>
          {agencyData ? `Set Your Password for ${agencyData.name}` : 'Set Your Password'}
        </Typography>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField 
            label="New Password" 
            name="password" 
            value={form.password} 
            onChange={handleChange} 
            fullWidth 
            margin="normal" 
            required 
            type="password"
            error={!!error && !requirements.length}
            helperText={error && !requirements.length ? 'Password must be at least 8 characters long' : ''}
          />
          <TextField 
            label="Confirm Password" 
            name="confirmPassword" 
            value={form.confirmPassword} 
            onChange={handleChange} 
            fullWidth 
            margin="normal" 
            required 
            type="password"
            error={!!error && form.password !== form.confirmPassword}
            helperText={error && form.password !== form.confirmPassword ? 'Passwords do not match' : ''}
          />
          
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>Password Requirements:</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  {requirements.length ? <CheckCircle color="success" /> : <Error color="error" />}
                </ListItemIcon>
                <ListItemText primary="At least 8 characters long" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {requirements.uppercase ? <CheckCircle color="success" /> : <Error color="error" />}
                </ListItemIcon>
                <ListItemText primary="At least one uppercase letter" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {requirements.lowercase ? <CheckCircle color="success" /> : <Error color="error" />}
                </ListItemIcon>
                <ListItemText primary="At least one lowercase letter" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {requirements.number ? <CheckCircle color="success" /> : <Error color="error" />}
                </ListItemIcon>
                <ListItemText primary="At least one number" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {requirements.special ? <CheckCircle color="success" /> : <Error color="error" />}
                </ListItemIcon>
                <ListItemText primary="At least one special character (!@#$%^&*(),.?&quot;:{}|&lt;&gt;)" />
              </ListItem>
            </List>
          </Box>

          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth 
            sx={{ mt: 2 }} 
            disabled={loading || !Object.values(requirements).every(Boolean)}
          >
            {loading ? 'Setting Password...' : 'Set Password'}
          </Button>
        </form>
        
        {agencyData && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isIndividual
                ? "After setting your password, sign in at:"
                : "After setting your password, you'll have access to your agency dashboard at:"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
              {isIndividual ? `${window.location.origin}/individual/login` : `${window.location.origin}/${agencyData.slug}`}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SetPassword; 